import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Plus, Calculator, Timer, Search, Loader2, RefreshCw, Download, X as XIcon, UserPlus, CheckSquare } from 'lucide-react';
import { incidentAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime } from '../utils/timezone';
import { toast } from 'sonner';
import Paginator from '../components/ui/Paginator';

const MIN = 60 * 1000;
const HOUR = 60 * MIN;

function formatCountdown(ms) {
  const abs = Math.abs(ms);
  const sign = ms < 0 ? '-' : '';
  const h = Math.floor(abs / HOUR);
  const m = Math.floor((abs % HOUR) / MIN);
  const s = Math.floor((abs % MIN) / 1000);
  if (h > 0) return `${sign}${h}h ${m}m ${s}s`;
  if (m > 0) return `${sign}${m}m ${s}s`;
  return `${sign}${s}s`;
}

function SLATimer({ due }) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!due) return;
    const update = () => setRemaining(new Date(due).getTime() - Date.now());
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, [due]);

  if (!due) return <span className="text-xs text-slate-400">N/A</span>;
  const isBreached = remaining < 0;
  const atRisk = remaining > 0 && remaining < 30 * MIN;
  const color = isBreached ? 'text-rose-600' : atRisk ? 'text-amber-600' : 'text-emerald-600';
  const bg = isBreached ? 'bg-rose-50 ring-rose-200' : atRisk ? 'bg-amber-50 ring-amber-200' : 'bg-emerald-50 ring-emerald-200';

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${bg} ring-1`}>
      {isBreached && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span></span>}
      <Timer className={`w-3 h-3 ${color}`} />
      <span className={`text-xs font-mono font-semibold ${color} tabular-nums`}>{formatCountdown(remaining)}</span>
    </div>
  );
}

const prClass = { p1: 'bg-rose-100 text-rose-800', p2: 'bg-amber-100 text-amber-800', p3: 'bg-blue-100 text-blue-800', p4: 'bg-slate-200 text-slate-700' };
const stClass = { new: 'bg-slate-100 text-slate-700', assigned: 'bg-blue-100 text-blue-800', in_progress: 'bg-emerald-100 text-emerald-800', pending_user: 'bg-amber-100 text-amber-800', resolved: 'bg-emerald-200 text-emerald-900', closed: 'bg-slate-200 text-slate-600' };

export default function IncidentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEndUser = (user?.role_name || '').toLowerCase().replace(/\s+/g, '_') === 'end_user';
  const [incidents, setIncidents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [bulkAssignTo, setBulkAssignTo] = useState('');

  const fetchIncidents = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      const [res, uRes] = await Promise.all([incidentAPI.list(params), userAPI.list({ page_size: 500 })]);
      setIncidents(res.data?.results || res.data || []);
      setTotal(res.data?.count || 0);
      setUsers(uRes.data?.results || uRes.data || []);
      setSelected(new Set());
    } catch {
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchIncidents(); // eslint-disable-next-line
  }, [page, pageSize]);

  const toggleSelect = (id) => {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleAll = () => {
    if (selected.size === incidents.length) setSelected(new Set());
    else setSelected(new Set(incidents.map(i => i.id)));
  };

  const handleBulk = async (action, extra = {}) => {
    if (selected.size === 0) { toast.error('Select tickets first'); return; }
    try {
      await incidentAPI.bulkAction({ ids: [...selected], action, ...extra });
      toast.success(`${selected.size} tickets: ${action}`);
      fetchIncidents();
    } catch { toast.error('Bulk action failed'); }
  };

  const exportCSV = () => {
    const data = incidents.filter(i => selected.size === 0 || selected.has(i.id));
    const headers = ['Ticket', 'Title', 'Priority', 'Status', 'Assignee', 'Impact', 'Urgency', 'Created'];
    const rows = data.map(i => [i.ticket_number, i.title, i.priority, i.status, i.assigned_to_name || '', i.impact, i.urgency, i.created_at ? new Date(i.created_at).toLocaleString() : '']);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `incidents_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchIncidents();
  };

  return (
    <div data-testid="incidents-page">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <AlertCircle className="w-6 h-6 text-rose-600" /> Incident Management
        </h2>
        <div className="flex gap-2">
          <button onClick={fetchIncidents} className="bg-white dark:bg-slate-800 border dark:border-slate-700 px-3 py-2 rounded-xl text-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:text-slate-300" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('/new-request')} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600" data-testid="new-incident-btn">
            <Plus className="w-4 h-4" /> New Incident
          </button>
        </div>
      </div>

      <div className="bg-slate-100 dark:bg-slate-800 border dark:border-slate-700 p-3 rounded-lg text-xs mb-4 flex items-center gap-2 text-slate-700 dark:text-slate-300">
        <Calculator className="w-4 h-4 text-sky-600 dark:text-sky-400 flex-shrink-0" /> Priority = Impact × Urgency (ITIL matrix) · SLA timers update in real-time
      </div>

      {/* Search + Bulk Actions */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search incidents..." className="w-full pl-9 pr-3 py-2.5 border dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 dark:text-white" data-testid="search-incidents" />
          </div>
          <button type="submit" className="bg-white dark:bg-slate-800 border dark:border-slate-700 px-4 py-2 rounded-xl text-sm hover:bg-slate-50">Search</button>
        </form>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 bg-sky-50 dark:bg-sky-900/30 px-4 py-2 rounded-xl border border-sky-200 dark:border-sky-700">
            <CheckSquare className="w-4 h-4 text-sky-600" />
            <span className="text-sm font-medium text-sky-700 dark:text-sky-400">{selected.size} selected</span>
            {!isEndUser && (
              <>
                <select value={bulkAssignTo} onChange={e => setBulkAssignTo(e.target.value)} className="border dark:border-slate-700 rounded-lg px-2 py-1 text-xs dark:bg-slate-800 dark:text-white">
                  <option value="">Assign to...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                </select>
                {bulkAssignTo && <button onClick={() => handleBulk('assign', { assigned_to: bulkAssignTo })} className="bg-sky-500 text-white px-2 py-1 rounded-lg text-xs">Assign</button>}
                <button onClick={() => handleBulk('close')} className="bg-emerald-500 text-white px-2 py-1 rounded-lg text-xs" data-testid="bulk-close">Close</button>
                <button onClick={() => handleBulk('status', { status: 'in_progress' })} className="bg-amber-500 text-white px-2 py-1 rounded-lg text-xs">In Progress</button>
              </>
            )}
            <button onClick={exportCSV} className="bg-slate-600 text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1"><Download className="w-3 h-3" /> CSV</button>
            <button onClick={() => setSelected(new Set())} className="text-slate-400 hover:text-slate-600"><XIcon className="w-4 h-4" /></button>
          </div>
        )}
        {selected.size === 0 && (
          <button onClick={exportCSV} className="bg-white dark:bg-slate-800 border dark:border-slate-700 px-3 py-2 rounded-xl text-sm hover:bg-slate-50 flex items-center gap-1.5" data-testid="export-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : incidents.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-12 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No incidents found</p>
          <button onClick={() => navigate('/new-request')} className="mt-4 bg-sky-500 text-white px-4 py-2 rounded-xl text-sm hover:bg-sky-600">Create First Incident</button>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
            <table className="w-full" data-testid="incidents-table">
              <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-3 w-10"><input type="checkbox" checked={selected.size === incidents.length && incidents.length > 0} onChange={toggleAll} className="rounded" /></th>
                  <th className="px-4 py-3 text-left font-semibold">ID</th>
                  <th className="px-4 py-3 text-left font-semibold">Title</th>
                  <th className="px-4 py-3 text-left font-semibold">Priority</th>
                  <th className="px-4 py-3 text-left font-semibold">Assignee</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-center font-semibold">Response SLA</th>
                  <th className="px-4 py-3 text-center font-semibold">Resolution SLA</th>
                  <th className="px-4 py-3 text-left font-semibold">Created</th>
                  <th className="px-4 py-3 text-left font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y text-sm">
                {incidents.map((inc) => (
                  <tr key={inc.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer" data-testid={`row-${inc.ticket_number}`}>
                    <td className="px-3 py-3"><input type="checkbox" checked={selected.has(inc.id)} onChange={() => toggleSelect(inc.id)} onClick={e => e.stopPropagation()} className="rounded" /></td>
                    <td className="px-4 py-3 font-mono text-rose-700 dark:text-rose-400 font-semibold" onClick={() => navigate(`/ticket/incident/${inc.id}`)}>{inc.ticket_number}</td>
                    <td className="px-4 py-3" onClick={() => navigate(`/ticket/incident/${inc.id}`)}>
                      <div className="font-medium dark:text-white">{inc.title}</div>
                      <div className="text-xs text-slate-400">{inc.impact} · {inc.urgency}</div>
                    </td>
                    <td className="px-4 py-3"><span className={`${prClass[inc.priority] || 'bg-slate-100'} px-2.5 py-1 rounded-full text-xs font-semibold`}>{inc.priority?.toUpperCase()}</span></td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{inc.assigned_to_name || 'Unassigned'}</td>
                    <td className="px-4 py-3"><span className={`${stClass[inc.status] || 'bg-slate-100 text-slate-700'} px-2 py-1 rounded-full text-xs`}>{inc.status?.replace(/_/g, ' ')}</span></td>
                    <td className="px-4 py-3 text-center">
                      {(inc.first_response_at || inc.sla_response_met !== null || ['assigned', 'in_progress', 'resolved', 'closed', 'pending_user'].includes(inc.status))
                        ? <span className={`text-xs px-2 py-1 rounded-lg ${inc.sla_response_met === false ? 'text-rose-600 bg-rose-50' : 'text-emerald-600 bg-emerald-50'}`}>{inc.sla_response_met === false ? 'Breached' : 'Responded'}</span>
                        : <SLATimer due={inc.response_due} />}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {inc.sla_paused
                        ? <span className="text-xs px-2 py-1 rounded-lg text-amber-600 bg-amber-50">Paused</span>
                        : (['resolved', 'closed'].includes(inc.status) || inc.sla_resolution_met !== null)
                          ? <span className={`text-xs px-2 py-1 rounded-lg ${inc.sla_resolution_met === false ? 'text-rose-600 bg-rose-50' : inc.sla_resolution_met === true ? 'text-emerald-600 bg-emerald-50' : 'text-slate-500 bg-slate-50'}`}>{inc.sla_resolution_met === false ? 'Breached' : inc.sla_resolution_met === true ? 'Met' : 'Closed'}</span>
                          : <SLATimer due={inc.resolution_due} />}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400 dark:text-slate-500">{formatDateTime(inc.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-400 dark:text-slate-500">{formatDateTime(inc.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {incidents.map(inc => (
              <div key={inc.id} onClick={() => navigate(`/ticket/incident/${inc.id}`)}
                className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-4 shadow-sm cursor-pointer active:bg-slate-50"
                data-testid={`row-${inc.ticket_number}`}>
                <div className="flex items-start justify-between mb-2">
                  <span className="font-mono text-rose-700 dark:text-rose-400 font-bold text-sm">{inc.ticket_number}</span>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    <span className={`${prClass[inc.priority] || 'bg-slate-100'} px-2 py-0.5 rounded-full text-xs font-semibold`}>{inc.priority?.toUpperCase()}</span>
                    <span className={`${stClass[inc.status] || 'bg-slate-100'} px-2 py-0.5 rounded-full text-xs`}>{inc.status?.replace(/_/g, ' ')}</span>
                  </div>
                </div>
                <div className="font-medium text-slate-800 dark:text-white text-sm mb-1 leading-snug">{inc.title}</div>
                <div className="text-xs text-slate-400 mb-2">{inc.impact} · {inc.urgency}</div>
                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span>{inc.assigned_to_name || 'Unassigned'}</span>
                  <SLATimer due={inc.resolution_due} />
                </div>
              </div>
            ))}
          </div>
          {/* Paginator */}
          <div className="mt-3">
            <Paginator total={total} page={page} pageSize={pageSize} onPage={p => { setPage(p); }} onPageSize={s => { setPageSize(s); setPage(1); }} />
          </div>
        </>
      )}
    </div>
  );
}
